import float16 from '../assets/libraries/float16';
import { Nullable } from '@/types/utility';
import { createTaggedLogger } from '@/services/logger';
import { sleep } from '@/utils/timeoutHelpers';
import { sendInfoEvent } from '@/utils/sentryHelpers';

const logger = createTaggedLogger('deviceDataConvertors');

enum BinaryFormat {
  F16,
  F32,
}

const fetchWithRetry = async (url: string, attempts = 5, delayMs = 3000): Promise<Response> => {
  let lastError: unknown;

  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const response = await fetch(url);

      if (response.ok) {
        return response;
      }

      lastError = new Error(`Network response was not ok: ${response.status}`);
    } catch (error) {
      lastError = error;
    }

    const errorMessage = lastError instanceof Error ? lastError.message : String(lastError);
    logger.warn('Failed to fetch measurement data', {
      url,
      attempt,
      attempts,
      error: errorMessage,
    });

    sendInfoEvent('Failed to fetch measurement data from CDN', [
      ['url', url],
      ['attempt', attempt],
      ['attempts', attempts],
      ['error', errorMessage],
    ]);

    if (attempt < attempts) {
      await sleep(delayMs);
    }
  }

  const errorMessage = lastError instanceof Error ? lastError.message : String(lastError);

  logger.error('Exceeded retry attempts for measurement data fetch', {
    url,
    attempts,
    error: errorMessage,
  });

  throw lastError instanceof Error ? lastError : new Error('Failed to fetch');
};

const convertArrayBufferToFloat32 = (
  format: Nullable<BinaryFormat>,
  buffer: ArrayBuffer,
): Float32Array => {
  if (format === BinaryFormat.F32) {
    return new Float32Array(buffer);
  } else if (format === BinaryFormat.F16) {
    // @ts-ignore
    const float16Array = new float16.Float16Array(buffer);
    return Float32Array.from(float16Array);
  }

  return new Float32Array(new ArrayBuffer(0));
};

export const convertDataToMau = (base64: string = '') => {
  const binaryString = window.atob(base64);

  const int8Array = new Int8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    int8Array[i] = binaryString.charCodeAt(i);
  }

  const binaryFormat = BinaryFormat.F32;
  const float32Array = convertArrayBufferToFloat32(binaryFormat, int8Array.buffer);

  return [...float32Array];
};

const getFilenameFromURL = (url: string) => {
  const urlObj = new URL(url);
  const pathname = urlObj.pathname;
  return pathname.split('/').pop();
};

const getFullExtension = (filename: Nullable<string>) => {
  // This regex captures from the first dot in a sequence of two or more file extensions to the end
  const result = filename?.match(/(\.[0-9a-z]+)+$/i);

  if (result) {
    return result[0];
  }
  return ''; // Return an empty string if no extension is found
};

const getBinaryFormatFromExtension = (extension: string) => {
  if (extension === '.bin') {
    return BinaryFormat.F16;
  } else if (extension === '.f32.bin') {
    return BinaryFormat.F32;
  }
  return null;
};

const replaceLocalhostWithDockerHost = (url: string): string => {
  // TODO use env
  // Only for testing
  // Only replace localhost if we're on the report page
  if (url.includes('localhost') && window.location.href.includes('/share/#/reports/')) {
    return url.replace('localhost', 'host.docker.internal');
  }
  return url;
};

export const getDataMeasurementFromCDN = async (url: string) => {
  const processedUrl = replaceLocalhostWithDockerHost(url);

  const response = await fetchWithRetry(processedUrl);

  const arrayBuffer = await response.arrayBuffer();

  const filename = getFilenameFromURL(processedUrl);
  const extension = getFullExtension(filename);
  const binaryFormat = getBinaryFormatFromExtension(extension);

  const float32Array = convertArrayBufferToFloat32(binaryFormat, arrayBuffer);

  const result = [...float32Array];
  return result;
};
